// Axios Setup

const apiURL = "https://api.ecoplore.com/";

const api = axios.create({
  baseURL: apiURL,
});

api.interceptors.request.use(
  (config) => {
    const token = window.localStorage.getItem("token");
    if (token) {
      config.headers.authorization = token;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

function deleteSession() {
  store.commit("deleteUser", true);
  store.commit("deleteCart");
}

api.interceptors.response.use(
  (res) => {
    return res;
  },
  (err) => {
    const originalConfig = err.config;
    if (originalConfig.url !== "user/v1/login" && err.response) {
      if (err.response.status === 401 && !originalConfig._retry) {
        originalConfig._retry = true;

        const refreshToken = window.localStorage.getItem("refreshToken");
        if (refreshToken) {
          return axios
            .get(`${apiURL}user/v1/refresh`, {
              headers: {
                Authorization: refreshToken,
              },
            })
            .then(({ data }) => {
              window.localStorage.setItem("token", data.token);
              window.localStorage.setItem("refreshToken", data.refresh);
              return api(originalConfig);
            })
            .catch(() => {
              deleteSession();
              return Promise.reject();
            });
        } else {
          deleteSession();
          return Promise.reject();
        }
      }
    }
    return Promise.reject(err);
  }
);

// Utility Functions

function validateEmail(email) {
  const regex =
    /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return regex.test(email);
}

function validateMobile(mobile) {
  const regex = /^\d{10}$/;
  return regex.test(mobile);
}

function validateOTP(otp) {
  const regex = /^\d{6}$/;
  return regex.test(otp);
}

function validatePassword(password) {
  const regex = /^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d]{8,}$/;
  return regex.test(password);
}

function closeModal() {
  document.querySelector(".mfp-close").click();
}

function toastSuccess(message) {
  vt.success(message, {
    position: "bottom-center",
    duration: 5000,
  });
}

function toastError(message) {
  vt.error(message, {
    position: "bottom-center",
    duration: 5000,
  });
}

// Vue JS Code here

// VueX Store

const store = new Vuex.Store({
  state: {
    user: window.localStorage.getItem("user")
      ? JSON.parse(window.localStorage.getItem("user"))
      : null,
    token: window.localStorage.getItem("token") || null,
    login: window.localStorage.getItem("login")
      ? JSON.parse(window.localStorage.getItem("login"))
      : false,
    cart: window.sessionStorage.getItem("cart")
      ? JSON.parse(window.sessionStorage.getItem("cart"))
      : [],
    currentHotel: window.sessionStorage.getItem("currentHotel")
      ? JSON.parse(window.sessionStorage.getItem("currentHotel"))
      : null,
    currentRooms: window.sessionStorage.getItem("currentRooms")
      ? JSON.parse(window.sessionStorage.getItem("currentRooms"))
      : null,
    currentDate: window.sessionStorage.getItem("currentDate")
      ? JSON.parse(window.sessionStorage.getItem("currentDate"))
      : null,
    currentGuests: window.sessionStorage.getItem("currentGuests")
      ? JSON.parse(window.sessionStorage.getItem("currentGuests"))
      : null,
  },
  mutations: {
    updateUser(state, { user, token, refreshToken }) {
      window.localStorage.setItem("user", JSON.stringify(user));
      window.localStorage.setItem("login", "true");
      state.user = user;
      state.login = true;
      if (token && refreshToken) {
        window.localStorage.setItem("token", token);
        window.localStorage.setItem("refreshToken", refreshToken);
        state.token = token;
        toastSuccess("Successfully logged in");
        closeModal();
      }
    },
    deleteUser(state, invalidSession) {
      window.localStorage.removeItem("user");
      window.localStorage.removeItem("token");
      window.localStorage.removeItem("login");
      window.localStorage.removeItem("refreshToken");
      state.user = null;
      state.token = null;
      state.login = false;
      if (invalidSession) {
        toastError("Session Expired");
      } else {
        toastSuccess("Successfully logged out");
      }
    },
    resetCart(state, { hotel, rooms, date, guests }) {
      state.cart = [];
      state.currentHotel = hotel;
      state.currentRooms = rooms;
      state.currentDate = date;
      state.currentGuests = guests;
      window.sessionStorage.removeItem("cart");
      window.sessionStorage.setItem("currentHotel", JSON.stringify(hotel));
      window.sessionStorage.setItem("currentRooms", JSON.stringify(rooms));
      window.sessionStorage.setItem("currentDate", JSON.stringify(date));
      window.sessionStorage.setItem("currentGuests", JSON.stringify(guests));
    },
    increaseCartQuanity(state, { roomID, qty = 1, image }) {
      const room = state.currentRooms.find((room) => room.roomId === roomID);

      const cartQuantity =
        state.cart.find((cartItem) => cartItem.roomType === room._id)?.count ||
        0;

      if (room.available >= cartQuantity + qty && cartQuantity !== 0) {
        state.cart = state.cart.map((cartItem) =>
          cartItem.roomType === room._id
            ? { ...cartItem, count: cartItem.count + qty }
            : cartItem
        );
      } else if (room.available >= cartQuantity + qty && cartQuantity === 0) {
        state.cart = [
          ...state.cart,
          {
            roomType: room._id,
            count: qty,
            room: { ...room, image },
          },
        ];
      } else {
        const roomAvailable = room.available - cartQuantity;
        toastError(
          `${
            roomAvailable !== 0 ? `Only ${roomAvailable}` : "No"
          } more room is available for this room type.`
        );
      }
      window.sessionStorage.setItem("cart", JSON.stringify(state.cart));
    },
    decreaseCartQuantity(state, roomID) {
      const room = state.currentRooms.find((room) => room.roomId === roomID);
      const cartQuantity =
        state.cart.find((cartItem) => cartItem.roomType === room._id)?.count ||
        0;

      if (cartQuantity > 1) {
        state.cart = state.cart.map((cartItem) =>
          cartItem.roomType === room._id
            ? { ...cartItem, count: cartItem.count - 1 }
            : cartItem
        );
      } else {
        state.cart = state.cart.filter(
          (cartItem) => cartItem.roomType !== room._id
        );
      }
      window.sessionStorage.setItem("cart", JSON.stringify(state.cart));
    },
    removeCartItem(state, roomID) {
      const room = state.currentRooms.find((room) => room.roomId === roomID);
      state.cart = state.cart.filter(
        (cartItem) => cartItem.roomType !== room._id
      );
      window.sessionStorage.setItem("cart", JSON.stringify(state.cart));
    },
    deleteCart(state) {
      state.cart = [];
      (state.currentHotel = null),
        (state.currentRooms = null),
        (state.currentDate = null),
        (state.guest = null),
        window.sessionStorage.removeItem("cart");
      window.sessionStorage.removeItem("currentHotel");
      window.sessionStorage.removeItem("currentRooms");
      window.sessionStorage.removeItem("currentDate");
      window.sessionStorage.removeItem("currentGuests");
    },
  },
});

// OTP Component

Vue.component("otp-form", {
  template: `
  <div>
  <div class="loginPopup_head">
  <h3>Enter OTP</h3>
  <p>Enter the OTP recieved in your email</p>
  </div>
  <form class="loginPopup_form" @submit.prevent="handleSubmit">
  <div class="inputForm">
  <label>OTP</label>
  <input type="text" :class="error !== '' ? 'is-invalid' : ''" required v-model="input">
  <span class="error-message" v-if="error">{{error}}</span>
  </div>
  <div class="inputForm">
  <input type="submit" :value="loading ? 'Loading' : 'Verify'" class="button solid" :disabled="loading">
  </div>
  </form>
  </div>
  `,
  props: ["url"],
  data() {
    return {
      input: "",
      error: "",
      loading: false,
    };
  },
  methods: {
    handleSubmit() {
      this.loading = true;
      const body = { otp: this.input };
      api
        .post(this.url, body)
        .then(({ data }) => {
          this.loading = false;
          if (data.error) {
            this.error = data.msg;
          } else {
            this.$emit("success", data);
          }
        })
        .catch((error) => {
          this.loading = false;
          if (error.response) {
            const { msg } = error.response.data;
            this.error = msg;
          } else {
            this.error = "Can't verify OTP";
          }
        });
    },
  },
  watch: {
    input: function (value) {
      if (!value) {
        this.error = "OTP is required";
      } else if (!validateOTP(value)) {
        this.error = "Enter OTP received in your email";
      } else {
        this.error = "";
      }
    },
  },
});

// Footer Booking Instance

const footerBookingApp = new Vue({
  el: '#footerBooking',
  data: {
    isUser: false,
  },
  computed: {
    user() {
      return store.state.user
    },
    token() {
      return store.state.token
    },
  },
  methods: {
    isUserFn() {
      if (this.user !== null) {
        this.isUser = true
      }
    },
  },
  created() {
    this.isUserFn()
  },
})

// Footer Booking my account Instance

const myAccountView = new Vue({
  el: '#myAccountView',
  data: {
    account: {
      name: '',
      email: '',
      mobile: '',
    },
  },
  computed: {
    user() {
      return store.state.user
    },
    token() {
      return store.state.token
    },
  },
  methods: {
    acountDataFn() {
      if (this.user !== null) {
        this.account = {
          name: this.user.name,
          email: this.user.email,
          mobile: this.user.mobile,
        }
      }
    },
  },
  created() {
    this.acountDataFn()
  },
})

// Right Menu Instance

const rightMenuApp = new Vue({
  el: "#right-menu",
  data: {
    openAuthMenu: false,
    openSupportMenu: false,
  },
  computed: {
    user() {
      return store.state.user;
    },
    login() {
      return store.state.login;
    },
    token() {
      return store.state.token;
    },
  },
  methods: {
    logout() {
      store.commit("deleteUser");
      store.commit("deleteCart");
    },
    toggleAuthMenu() {
      this.openAuthMenu = !this.openAuthMenu;
    },
    toggleSupportMenu() {
      this.openSupportMenu = !this.openSupportMenu;
    },
  },
});

// Login Popup

const loginApp = new Vue({
  el: "#signin-form",
  data: {
    email: "",
    password: "",
    error: "",
    otpStep: false,
    loading: false,
  },
  methods: {
    handleLogin() {
      this.loading = true;
      this.error = "";
      const body = {
        email: this.email,
        password: this.password,
      };
      api
        .post("user/v1/login", body)
        .then(({ data }) => {
          this.loading = false;
          if (data.error) {
            this.error = data.msg;
          } else if (data.data.active === false) {
            window.localStorage.setItem("token", data.token);
            this.otpStep = true;
          } else {
            this.loginUser(data);
          }
        })
        .catch((error) => {
          this.loading = false;
          if (error.response) {
            const { msg } = error.response.data;
            this.error = msg;
          } else {
            this.error = "Can't authenticate";
          }
        });
    },
    loginUser(data) {
      const payload = {
        user: data.data,
        token: data.token,
        refreshToken: data.refresh,
      };
      store.commit("updateUser", payload);
      (this.email = ""), (this.password = ""), (this.otpStep = false);
    },
  },
});

// Forgot Password Popup

const forgotApp = new Vue({
  el: "#forgot-password-form",
  data: {
    email: "",
    error: "",
    form: {
      password: "",
      repeatPassword: "",
    },
    formErrors: {
      password: "",
      repeatPassword: "",
    },
    step: "email",
    loading: false,
  },
  methods: {
    sendOTP() {
      this.loading = true;
      this.error = "";
      const body = {
        email: this.email,
      };
      api
        .post("user/v1/forgot/password", body)
        .then(({ data }) => {
          this.loading = false;
          if (data.error) {
            this.error = data.msg;
          } else {
            window.localStorage.setItem("token", data.token);
            this.step = "otp";
            this.email = "";
            toastSuccess("Successfully sent OTP to your email address");
          }
        })
        .catch((error) => {
          this.loading = false;
          if (error.response) {
            const { msg } = error.response.data;
            this.error = msg;
          } else {
            this.error = "Cannot send OTP";
          }
        });
    },
    otpSuccess(data) {
      window.localStorage.setItem("token", data.token);
      this.step = "password";
    },
    updatePassword() {
      this.error = "";
      this.loading = true;
      api
        .put("user/v1/set/password", this.form)
        .then(({ data }) => {
          this.loading = false;
          if (data.error) {
            this.error = data.msg;
          } else {
            toastSuccess("Password successfully reset");
            closeModal();
            this.step = "email";
            this.form = {
              password: "",
              repeatPassword: "",
            };
          }
        })
        .catch((error) => {
          this.loading = false;
          if (error.response) {
            const { msg } = error.response.data;
            this.error = msg;
          } else {
            this.error = "Cannot update Password";
          }
        });
    },
  },
  watch: {
    "form.password": function (value) {
      if (!value) {
        this.formErrors.password = "Password is required";
      } else if (!validatePassword(value)) {
        this.formErrors.password =
          "Minimum eight characters, at least one letter and one number";
      } else {
        this.formErrors.password = "";
      }
    },
    "form.repeatPassword": function (value) {
      if (!value) {
        this.formErrors.repeatPassword = "Confirm Password is required";
      } else if (this.form.password !== value) {
        this.formErrors.repeatPassword = "Confirm Password does not match";
      } else {
        this.formErrors.repeatPassword = "";
      }
    },
  },
});

// Registration Popup

const registerApp = new Vue({
  el: "#signup-form",
  data: {
    form: {
      name: "",
      email: "",
      password: "",
      repeatPassword: "",
      mobile: "",
    },
    formErrors: {
      name: "",
      email: "",
      password: "",
      repeatPassword: "",
      mobile: "",
    },
    error: "",
    otpStep: false,
    loading: false,
  },
  methods: {
    handleRegister() {
      this.error = "";
      this.loading = true;

      const validForm = _.values(this.formErrors).every(_.isEmpty);
      if (!validForm) {
        this.error = "Please correct the form fields";
      }

      api
        .post("user/v1/register", this.form)
        .then(({ data }) => {
          this.loading = false;
          if (data.error) {
            this.error = data.msg;
          } else {
            window.localStorage.setItem("token", data.token);
            this.otpStep = true;
          }
        })
        .catch((error) => {
          this.loading = false;
          if (error.response) {
            this.error = error.response.data.msg;
          } else {
            this.error = "Can't create user";
          }
        });
    },
    loginUser(data) {
      const payload = {
        user: data.data,
        token: data.token,
        refreshToken: data.refresh,
      };
      store.commit("updateUser", payload);
      this.form = {
        name: "",
        email: "",
        password: "",
        repeatPassword: "",
        mobile: "",
      };
      this.otpStep = false;
    },
  },
  watch: {
    "form.name": function (value) {
      if (!value) {
        this.formErrors.name = "Name is required";
      } else {
        this.formErrors.name = "";
      }
    },
    "form.email": function (value) {
      if (!value) {
        this.formErrors.email = "Email is required";
      } else if (!validateEmail(value)) {
        this.formErrors.email = "Email is invalid";
      } else {
        this.formErrors.email = "";
      }
    },
    "form.password": function (value) {
      if (!value) {
        this.formErrors.password = "Password is required";
      } else if (!validatePassword(value)) {
        this.formErrors.password =
          "Minimum eight characters, at least one letter and one number";
      } else {
        this.formErrors.password = "";
      }
    },
    "form.repeatPassword": function (value) {
      if (!value) {
        this.formErrors.repeatPassword = "Confirm Password is required";
      } else if (this.form.password !== value) {
        this.formErrors.repeatPassword = "Confirm Password does not match";
      } else {
        this.formErrors.repeatPassword = "";
      }
    },
    "form.mobile": function (value) {
      if (!value) {
        this.formErrors.mobile = "Mobile is required";
      } else if (!validateMobile(value)) {
        this.formErrors.mobile = "Enter a 10 Digit Mobile number";
      } else {
        this.formErrors.mobile = "";
      }
    },
  },
});

// Edit Profile Popup

const editProfileApp = new Vue({
  el: "#edit-profile-form",
  data: {
    form: {
      name: "",
      email: "",
      password: "",
      repeatPassword: "",
      mobile: "",
    },
    formErrors: {
      name: "",
      email: "",
      password: "",
      repeatPassword: "",
      mobile: "",
    },
    error: "",
    loading: false,
  },
  computed: {
    user() {
      return store.state.user;
    },
  },
  methods: {
    handleUpdateProfile() {
      this.error = "";
      this.loading = true;

      const validForm = _.values(this.formErrors).every(_.isEmpty);
      if (!validForm) {
        this.error = "Please correct the form fields";
      }

      const body = {
        name: this.form.name,
        email: this.form.email,
        mobile: this.form.mobile,
        ...(this.form.password ? { password: this.form.password } : {}),
      };

      api
        .put("user/v1/update/profile", body)
        .then(({ data }) => {
          this.loading = false;
          if (data.error) {
            this.error = data.msg;
            toastError("Update profile failed");
          } else {
            const payload = {
              user: data.data,
              token: data.token,
            };
            toastSuccess("Profile updated successfully");
            store.commit("updateUser", payload);
          }
        })
        .catch((error) => {
          this.loading = false;
          if (error.response) {
            this.error = error.response.data.msg;
          } else {
            this.error = "Can't update user";
          }
        });
    },
    resetForm() {
      if (this.user !== null) {
        this.form = {
          name: this.user.name,
          email: this.user.email,
          mobile: this.user.mobile,
          password: "",
          repeatPassword: "",
        };
      }
    },
  },
  created() {
    this.resetForm();
  },
  watch: {
    user: function () {
      this.resetForm();
    },
    "form.name": function (value) {
      if (!value) {
        this.formErrors.name = "Name is required";
      } else {
        this.formErrors.name = "";
      }
    },
    "form.email": function (value) {
      if (!value) {
        this.formErrors.email = "Email is required";
      } else if (!validateEmail(value)) {
        this.formErrors.email = "Email is invalid";
      } else {
        this.formErrors.email = "";
      }
    },
    "form.password": function (value) {
      if (value && !validatePassword(value)) {
        this.formErrors.password =
          "Minimum eight characters, at least one letter and one number";
      } else {
        this.formErrors.password = "";
      }
    },
    "form.repeatPassword": function (value) {
      if (value && this.form.password !== value) {
        this.formErrors.repeatPassword = "Confirm Password does not match";
      } else {
        this.formErrors.repeatPassword = "";
      }
    },
    "form.mobile": function (value) {
      if (!value) {
        this.formErrors.mobile = "Mobile is required";
      } else if (!validateMobile(value)) {
        this.formErrors.mobile = "Enter a 10 Digit Mobile number";
      } else {
        this.formErrors.mobile = "";
      }
    },
  },
});


// Single Hotel Instance

const singleHotelInstance = new Vue({
  el: "#single-hotel-page",
  data: {
    hotelID: "",
    form: {
      startDate: "",
      endDate: "",
      adultsRequired: 2,
      childrenRequired: 0,
    },
    formErrors: {
      adultsRequired: false,
      childrenRequired: false,
    },
    rooms: [],
    roomsLoaded: false,
    loading: false,
  },
  computed: {
    login() {
      return store.state.login;
    },
  },
  methods: {
    loadSession() {
      if (window.sessionStorage.getItem("sessionQuery")) {
        const session = JSON.parse(
          window.sessionStorage.getItem("sessionQuery")
        );
        this.form = {
          startDate: moment(session.startDate).format("DD-MM-YYYY"),
          endDate: moment(session.endDate).format("DD-MM-YYYY"),
          adultsRequired: session.adultsRequired,
          childrenRequired: session.childrenRequired,
        };
        this.checkAvailability();
      }
    },
    checkAvailability() {
      const body = {
        hotel: [this.hotelID],
        start: moment(this.form.startDate, "DD-MM-YYYY").format("YYYY/MM/DD"),
        end: moment(this.form.endDate, "DD-MM-YYYY").format("YYYY/MM/DD"),
      };

      const sessionQuery = {
        startDate: moment(this.form.startDate, "DD-MM-YYYY").toJSON(),
        endDate: moment(this.form.endDate, "DD-MM-YYYY").toJSON(),
        adultsRequired: parseInt(this.form.adultsRequired),
        childrenRequired: parseInt(this.form.childrenRequired),
      };

      window.sessionStorage.setItem(
        "sessionQuery",
        JSON.stringify(sessionQuery)
      );

      this.loading = true;

      api
        .post("hotel/check/available", body)
        .then(({ data }) => {
          this.loading = false;
          if (data.error) {
            toastError(data.msg);
            this.rooms = [];
          } else {
            if (data.room.length && data.room[0].hotel.active) {
              const currentHotel = data.room[0];
              this.rooms = currentHotel.rooms.map((room) => ({
                ...room,
                qty: 1,
              }));
              this.roomsLoaded = true;
              const payload = {
                hotel: {
                  ...currentHotel.hotel,
                  image: this.$el.dataset.image,
                  url: this.$el.dataset.url,
                },
                rooms: currentHotel.rooms,
                date: data.date,
                guests: {
                  adult: parseInt(this.form.adultsRequired),
                  children: parseInt(this.form.childrenRequired),
                },
              };
              store.commit("resetCart", payload);
            } else {
              this.roomsLoaded = true;
            }
          }
        })
        .catch((error) => {
          this.loading = false;
          this.rooms = [];
          if (error.response) {
            toastError(error.response.data.msg);
          } else {
            toastError("Can't check availability");
          }
          store.commit("deleteCart");
        });
    },
    increaseQty(roomID) {
      this.rooms = this.rooms.map((room) =>
        room.roomId === roomID && room.qty < room.available
          ? { ...room, qty: room.qty + 1 }
          : room
      );
    },
    decreaseQty(roomID) {
      this.rooms = this.rooms.map((room) =>
        room.roomId === roomID && room.qty > 1
          ? { ...room, qty: room.qty - 1 }
          : room
      );
    },
    increaseAdult() {
      this.form.adultsRequired = parseInt(this.form.adultsRequired) + 1;
    },
    decreaseAdult() {
      this.form.adultsRequired = parseInt(this.form.adultsRequired) - 1;
    },
    increaseChild() {
      this.form.childrenRequired = parseInt(this.form.childrenRequired) + 1;
    },
    decreaseChild() {
      this.form.childrenRequired = parseInt(this.form.childrenRequired) - 1;
    },
    addRoom(roomID, image) {
      const room = this.rooms.find((room) => room.roomId === roomID);
      store.commit("increaseCartQuanity", { roomID, qty: room.qty, image });
    },
  },
  mounted() {
    this.hotelID = this.$el.dataset.hotelId;
    this.loadSession();
  },
  watch: {
    'form.adultsRequired': function formAdultsRequired(value) {
      if (value <= 1) {
        this.formErrors.adultsRequired = true
        this.form.adultsRequired = 1
      } else {
        this.formErrors.adultsRequired = false
      }
    },
    'form.childrenRequired': function formChildrenRequired(value) {
      if (value <= 0) {
        this.formErrors.childrenRequired = true
        this.form.childrenRequired = 0
      } else {
        this.formErrors.childrenRequired = false
      }
    },
    
  }
});

// Floating mini cart app

Vue.component("cart", {
  template: `
  <div id="cart">
    <h3>Cart</h3>
    <div class="top-card">
      <img :src="currentHotel.image" class="cart-hotel-image" />
      <div class="top-card-details">
        <div class="cart-details">
          <label>Hotel:</label>
          <span>{{currentHotel.hotelName}}</span>
        </div>
        <div class="cart-details">
          <label>Check In:</label>
          <span>{{moment(currentDate.start, 'YYYY/MM/DD').format('DD-MM-YYYY')}}</span>
        </div>
        <div class="cart-details">
          <label>Check Out</label>
          <span>{{moment(currentDate.end, 'YYYY/MM/DD').format('DD-MM-YYYY')}}</span>
        </div>
        <div class="cart-details">
          <label>Guests:</label>
          <span>{{currentGuests.adult}} Adults {{currentGuests.children !== 0 ? '& ' + currentGuests.children + ' Children' : ''}}</span>
        </div>
      </div>
    </div>
    <h3>Rooms:</h3>
    <div class="room-card" v-for="item in cart" :key="item.room._id">
      <img :src="item.room.image" class="cart-hotel-image" />
      <div class="room-details">
        <div class="cart-details">
          <label>Name:</label>
          <span>{{item.room.roomType}}</span>
        </div>
        <div class="cart-details">
          <label>Guests Limit:</label>
          <span>{{item.room.maxOccupancy}} Adults & {{item.room.maxChildOccupancy !== 0 ? item.room.maxChildOccupancy + ' Children' : 'No Children'}}</span>
        </div>
        <div class="cart-details">
          <label>Price:</label>
          <span>₹{{item.room.roomPrice}} x {{numberOfDays}} Nights x {{item.count}} Rooms</span>
        </div>
        <div class="cart-details">
          <label>Subtotal:</label>
          <span>₹{{item.room.roomPrice * item.count * numberOfDays}}</span>
        </div>
      </div>
      <div class="room-quantity">
        <button @click="increaseCartQuanity" :data-room-id="item.room.roomId" class="cart-button" :disabled="item.count === item.room.available">+</button>
        <div class="quantity">{{item.count}}</div>
        <button @click="decreaseCartQuantity" :data-room-id="item.room.roomId" class="cart-button" :disabled="item.count === 1">-</button>
      </div>
      <div class="room-delete"><div class="remove-button" @click="removeCartItem" :data-room-id="item.room.roomId" >x</div></div>
    </div>
    <div class="total-details">
      <h3>Total:</h3>
      <h3>₹{{cart.reduce((total, item) => total + (item.room.roomPrice * item.count * numberOfDays), 0).toLocaleString()}}</h3>
    </div>
  </div>
  `,
  computed: {
    moment() {
      return window.moment;
    },
    cart() {
      return store.state.cart;
    },
    currentHotel() {
      return store.state.currentHotel;
    },
    currentRooms() {
      return store.state.currentRooms;
    },
    currentDate() {
      return store.state.currentDate;
    },
    currentGuests() {
      return store.state.currentGuests;
    },
    numberOfDays() {
      const startDate = moment(this.currentDate.start, "YYYY/MM/DD");
      const endDate = moment(this.currentDate.end, "YYYY/MM/DD");
      return endDate.diff(startDate, "days");
    },
  },
  methods: {
    increaseCartQuanity(event) {
      const roomID = event.target.dataset.roomId;
      store.commit("increaseCartQuanity", { roomID });
    },
    decreaseCartQuantity(event) {
      const roomID = event.target.dataset.roomId;
      store.commit("decreaseCartQuantity", roomID);
    },
    removeCartItem(event) {
      const roomID = event.target.dataset.roomId;
      store.commit("removeCartItem", roomID);
    },
  },
});

const floatingCart = new Vue({
  el: "#floating-cart",
  template: `
  <div class="floating-cart-container" v-if="cart.length && !window.location.pathname.includes('checkout')">
    <div class="floating-cart-content" v-if="open">
      <div class="floating-cart-heading" @click="toggle">Your Booking</div>
      <div class="floating-cart-content-inner">
        <cart></cart>
      </div>
      <a v-if="enoughRooms" href="/checkout" class="floating-cart-booking-button">
        Proceed to booking
      </a>
      <span v-else class="float-cart-error-message">Not enough rooms added for all the guests</span>
    </div>
    <div class="floating-cart-button" @click="toggle">
      <img src="/wp-content/themes/ecoplorecobold/assets/img/shopping-cart.png" class="floating-cart-icon" />
      <div class="floating-cart-count">{{cart.reduce((count, item) => count + item.count, 0)}}</div>
    </div>
  </div>
  `,
  data: {
    open: false,
  },
  computed: {
    window() {
      return window;
    },
    cart() {
      return store.state.cart;
    },
    currentGuests() {
      return store.state.currentGuests;
    },
    enoughRooms() {
      const roomCapacity = this.cart.reduce(
        (count, item) => ({
          adult: count.adult + item.count * item.room.maxOccupancy,
          children: count.children + item.count * item.room.maxChildOccupancy,
        }),
        { adult: 0, children: 0 }
      );
      return (
        roomCapacity.adult >= this.currentGuests.adult &&
        roomCapacity.children >= this.currentGuests.children
      );
    },
  },
  methods: {
    toggle() {
      this.open = !this.open;
    },
  },
});

// Checkout vue Instance

const checkoutInstance = new Vue({
  el: "#checkout-app",
  template: `
  <div class="checkout-container">
    <div v-if="loading" class="checkout-loader">
      <div class="spinner"></div>
    </div>
    <div class="row" v-if="cart.length">
      <div class="col-12 col-md-6">
        <cart v-if="cart.length" />
      </div>
      <div class="col-12 col-md-6">
        <form @submit.prevent="handlePayment" class="cart-form">
          <h3>Customer Details</h3>
          <div class="detail-group">
            <label>Name:</label>
            <span>{{user.name}}</span>
          </div>
          <div class="detail-group">
            <label>Guests:</label>
            <span>{{currentGuests.adult}} Adults {{currentGuests.children !== 0 ? '& ' + currentGuests.children + ' Children' : ''}}</span>
          </div>
          <div class="detail-group">
            <label>Check In Date:</label>
            <span>{{moment(currentDate.start, 'YYYY/MM/DD').format('DD-MM-YYYY')}}</span>
          </div>
          <div class="detail-group">
            <label>Check Out Date:</label>
            <span>{{moment(currentDate.end, 'YYYY/MM/DD').format('DD-MM-YYYY')}}</span>
          </div>
          <h3>Booking Summary</h3>
          <div class="detail-group">
            <label>Hotel:</label>
            <span>{{currentHotel.hotelName}}</span>
          </div>
          <div class="detail-group">
            <label>Rooms:</label>
            <div class="rooms-list">
              <div v-for="item in cart" :key="item.room._id" class="room-item">
                  <span>{{item.room.roomType}} x ({{item.count}})</span> 
                  <small>
                    <span>Price:</span> 
                    <span>₹{{item.room.roomPrice}} x {{numberOfDays}} nights</span>
                  </small>
                  <small>
                    <span>Subtotal:</span>
                    <span>₹{{item.room.roomPrice * item.count * numberOfDays}}</span>
                  </small>
              </div>
            </div>
          </div>
          <div class="detail-group">
          <h3>Total:</h3>
          <h3>₹{{total}}</h3>
          </div>
          <div class="form-group">
            <label>Notes</label>
            <textarea v-model="note" placeholder="Enter any additional notes..."></textarea>
          </div>
          <button type="submit" class="button solid" :disabled="!enoughRooms">
            <svg width="18" height="20" viewBox="0 0 18 20" fill="none" xmlns="http://www.w3.org/2000/svg" class="svelte-ekc7fv"><path d="M7.077 6.476l-.988 3.569 5.65-3.589-3.695 13.54 3.752.004 5.457-20L7.077 6.476z" fill="#fff" class="svelte-ekc7fv"></path><path d="M1.455 14.308L0 20h7.202L10.149 8.42l-8.694 5.887z" fill="#fff" class="svelte-ekc7fv"></path></svg>
            <span>Pay Now & Book</span>
          </button>
          <span v-if="!enoughRooms" class="error-message">Not enough rooms added for all the guests</span>
        </form>
      </div>
    </div>
    <div v-else-if="booking === null" class="empty-cart">
      <h2>No hotel selected for booking</h2>
      <a href="/hotels" class="button solid">Browse our hotels</a>
    </div>
    <div v-else-if="booking !== null" class="booking-success">
      <div class="row justify-content-center">
        <div class="col-12 col-md-6">
          <div class="success-message">
              <h2>Booking Successful</h2>
              <img src="/wp-content/themes/ecoplorecobold/assets/img/success.png" />
          </div>
          <div class="cart-form">
            <h3>Payment Details:</h3>
            <div class="detail-group">
              <label>Booking ID:</label>
              <span>{{booking.bookingId}}</span>
            </div>
            <div class="detail-group">
              <label>Payment ID:</label>
              <span>{{booking.paymentDetails.id}}</span>
            </div>
            <h3>Customer Details</h3>
            <div class="detail-group">
              <label>Name:</label>
              <span>{{user.name}}</span>
            </div>
            <div class="detail-group">
              <label>Guests:</label>
              <span>{{booking.guestCount.adult}} Adults {{booking.guestCount.children !== 0 ? '& ' + booking.guestCount.children + ' Children' : ''}}</span>
            </div>
            <div class="detail-group">
              <label>Check In Date:</label>
              <span>{{moment(booking.startDate).format('DD-MM-YYYY')}}</span>
            </div>
            <div class="detail-group">
              <label>Check Out Date:</label>
              <span>{{moment(booking.endDate).format('DD-MM-YYYY')}}</span>
            </div>
            <h3>Booking Summary</h3>
            <div class="detail-group">
              <label>Hotel:</label>
              <span>{{booking.hotelData.hotelName}}</span>
            </div>
            <div class="detail-group">
              <label>Rooms:</label>
              <div class="rooms-list">
                <div v-for="item in booking.roomRef" :key="item.room._id" class="room-item">
                    <span>{{item.room.roomType}} x ({{item.count}})</span> 
                    <small>
                      <span>Price:</span> 
                      <span>₹{{item.room.roomPrice}} x {{booking.daysCount}} nights</span>
                    </small>
                    <small>
                      <span>Subtotal:</span>
                      <span>₹{{item.room.roomPrice * item.count * booking.daysCount}}</span>
                    </small>
                </div>
              </div>
            </div>
            <div class="detail-group">
              <label>Notes:</label>
              <span>{{booking.note}}</span>
            </div>
            <div class="detail-group">
              <h3>Total:</h3>
              <h3>₹{{booking.bookingAmount.toLocaleString()}}</h3>
            </div>
          </div>
          <div class="link">
            <a href="/bookings" class="button solid">Go to My Bookings</a>
          </div>
        </div>
      </div>
    </div>
    <div class="payment-timer-container" v-if="booking === null && timerCount > 0">
      <div class="payment-timer">
        Payment Time Remaining: {{getTimerInMinutesAndSeconds}}
      </div>
    </div>
  </div>
  `,
  data: {
    loading: false,
    note: "",
    booking: null,
    timerCount: -1,
    paymentInstance: null,
  },
  computed: {
    user() {
      return store.state.user;
    },
    moment() {
      return window.moment;
    },
    cart() {
      return store.state.cart;
    },
    currentHotel() {
      return store.state.currentHotel;
    },
    currentRooms() {
      return store.state.currentRooms;
    },
    currentDate() {
      return store.state.currentDate;
    },
    currentGuests() {
      return store.state.currentGuests;
    },
    numberOfDays() {
      const startDate = moment(this.currentDate.start, "YYYY/MM/DD");
      const endDate = moment(this.currentDate.end, "YYYY/MM/DD");
      return endDate.diff(startDate, "days");
    },
    enoughRooms() {
      const roomCapacity = this.cart.reduce(
        (count, item) => ({
          adult: count.adult + item.count * item.room.maxOccupancy,
          children: count.children + item.count * item.room.maxChildOccupancy,
        }),
        { adult: 0, children: 0 }
      );
      return (
        roomCapacity.adult >= this.currentGuests.adult &&
        roomCapacity.children >= this.currentGuests.children
      );
    },
    total() {
      return this.cart
        .reduce(
          (total, item) =>
            total + item.room.roomPrice * item.count * this.numberOfDays,
          0
        )
        .toLocaleString();
    },
    getTimerInMinutesAndSeconds() {
      const minutes = Math.floor(this.timerCount / 60);
      const seconds = this.timerCount - minutes * 60;
      return `${minutes < 10 ? "0" + minutes : minutes}:${
        seconds < 10 ? "0" + seconds : seconds
      }`;
    },
  },
  methods: {
    handlePayment() {
      if (!this.enoughRooms) {
        return toastError("Not enough rooms for all the guests");
      }

      this.loading = true;

      const body = {
        start: this.currentDate.start,
        end: this.currentDate.end,
        hotel: this.currentHotel._id,
        mobile: this.user.mobile,
        name: this.user.name,
        room: this.cart,
        email: this.user.email,
        note: this.note,
        guestCount: {
          children: this.currentGuests.children,
          adult: this.currentGuests.adult,
        },
        hotelData: { ...this.currentHotel },
      };

      api
        .post("booking/create", body)
        .then(({ data }) => {
          this.loading = false;
          if (data.error) {
            toastError(data.msg);
          } else {
            const options = {
              key: data.payment.key,
              amount: data.payment.order.amount,
              currency: data.payment.order.currency,
              name: "Ecoplore",
              image: "/wp-content/themes/ecoplorecobold/assets/img/logo.png",
              order_id: data.payment.order.id,
              handler: function (response) {
                checkoutInstance.loading = true;
                const paymentBody = {
                  orderId: response.razorpay_order_id,
                  paymentId: response.razorpay_payment_id,
                };
                api
                  .post("booking/success", paymentBody)
                  .then(({ data }) => {
                    checkoutInstance.loading = false;
                    checkoutInstance.timerCount = -1;

                    if (data.error) {
                      toastError(data.msg);
                    } else {
                      checkoutInstance.booking = data.booking;
                      toastSuccess("Booking Successful");
                      store.commit("deleteCart");
                    }
                  })
                  .catch((error) => {
                    checkoutInstance.loading = false;
                    checkoutInstance.timerCount = -1;
                    if (error.response) {
                      const { msg } = error.response.data;
                      toastError(msg);
                    } else {
                      toastError("Booking Failed");
                    }
                  });
              },
              prefill: {
                name: this.user.name,
                email: this.user.email,
                contact: this.user.mobile,
              },
              notes: {
                note: this.note,
              },
              theme: {
                color: "#287108",
              },
              modal: {
                ondismiss: function () {
                  api.delete(`booking/${data.booking._id}`);
                  checkoutInstance.timerCount = -1;
                },
              },
            };
            this.paymentInstance = new Razorpay(options);
            this.paymentInstance.on("payment.failed", function (response) {
              toastError("Payment Failed");
            });
            this.paymentInstance.open();
            this.timerCount = 900;
          }
        })
        .catch((error) => {
          this.loading = false;
          if (error.response) {
            const { msg } = error.response.data;
            toastError(msg);
          } else {
            toastError("Can't create order.");
          }
        });
    },
  },
  watch: {
    timerCount: {
      handler(value) {
        if (value > 0) {
          setTimeout(() => {
            this.timerCount--;
          }, 1000);
        }
        if (value === 0) {
          if (this.booking === null) {
            this.paymentInstance.close();
            toastError("Booking Payment Time Expired");
          }
        }
      },
    },
  },
});

// My bookings app
const bookingsInstance = new Vue({
  el: "#bookings-app",
  template: `
  <div class="bookings-container">
    <div v-if="loading" class="bookings-loader">
      <div class="spinner"></div>
    </div>
    <div v-if="bookings.length === 0 && !loading" class="empty">
      <h2>You don't have any bookings</h2>
      <a href="/hotels" class="button solid">Browse our hotels</a>
    </div>
    <div v-if="bookings.length" class="bookings-list">
      <h2>My Bookings</h2>
      <div class="booking-item" v-for="booking in bookings">
        <div class="hotel-details">
          <img class="hotel-image" :src="booking.hotelRef?.coverImage || booking.hotelData?.image || '/wp-content/themes/ecoplorecobold/assets/img/412x450-1.png'" :alt="booking.hotelRef.hotelName" />
          <div class="hotel-info-list">
            <div class="hotel-info booking-id">ID: #{{booking.bookingId}}</div>
            <div class="hotel-info hotel-name">
              <a target="_blank" :href="booking.hotelRef?.url || booking.hotelData?.url">{{booking.hotelRef.hotelName}}</a>
            </div>
            <div class="hotel-info address">{{booking.hotelRef.location.address}}</div>
            <div class="hotel-info links" v-if="booking.status === 'CONFIRMED' || booking.status === 'COMPLETED' || booking.status === 'BOOKED'">
                <a target="_blank" :href="'https://maps.google.com/?q=' + booking.hotelRef.location.latitude + ',' + booking.hotelRef.location.longitude">
                <img src="/wp-content/themes/ecoplorecobold/assets/img/map-pin.png" />
                <span>Open Map</span>
              </a>
              <a target="_blank" :href="'tel:+91' + booking.admin.mobile ">
                <img src="/wp-content/themes/ecoplorecobold/assets/img/telephone.png" />
                <span>Call Hotel</span>
              </a>
            </div>
          </div>
        </div>
        <div class="booking-details">
          <div class="booking-info">
            <label>CHECK IN</label>
            <span>{{moment(booking.startDate).format('DD-MM-YYYY')}}</span>
          </div>
          <div class="booking-info">
            <label>CHECK OUT</label>
            <span>{{moment(booking.endDate).format('DD-MM-YYYY')}}</span>
          </div>
          <div class="booking-info">
            <label>GUESTS</label>
            <span>{{(booking.guestCount?.adult || 0) + (booking.guestCount?.children || 0 )  }}</span>
          </div>
          <div class="booking-info">
            <label>STATUS</label>
            <span>{{booking.status.split('_').join(' ')}}</span>
          </div>
          <div class="booking-info">
            <label>Cost</label>
            <span>₹{{booking.bookingAmount.toLocaleString()}}</span>
          </div>
        </div>
        <div class="booking-actions">
          <a target="_blank" :href="booking.hotelRef?.url || booking.hotelData?.url" class="button solid" :disabled="!booking.hotelRef?.url && !booking.hotelData?.url">View Hotel</a>
          <button class="button solid" @click="showBooking(booking._id)">View Details</button>
          <button v-if="booking.status === 'CONFIRMED'" class="button solid cancel" @click="confirmCancelBooking(booking._id)">Cancel</button>
        </div>
      </div>
      <div class="load-more-button">
        <button v-if="totalItems > bookings.length " class="button solid" @click="getBookings">Load More</button>
      </div>
    </div>
    <div v-if="modalBooking">
      <div class="mfp-bg mfp-ready"></div>
      <div class="mfp-wrap mfp-close-btn-in mfp-auto-cursor mfp-ready" tabindex="-1" style="overflow: hidden auto;">
        <div class="mfp-container mfp-s-ready mfp-inline-holder">
          <div class="mfp-content">
            <div id="edit-profile-form" class="loginPopup smallWidth">
                  <div class="loginPopup_head">
                  </div>
                  <div class="booking-popup-details">
                    <h3>Payment Details:</h3>
                    <div class="detail-group">
                      <label>Booking ID:</label>
                      <span>{{modalBooking.bookingId}}</span>
                    </div>
                    <div class="detail-group">
                      <label>Payment ID:</label>
                      <span>{{modalBooking.paymentDetails?.id}}</span>
                    </div>
                    <h3>Customer Details</h3>
                    <div class="detail-group">
                      <label>Name:</label>
                      <span>{{modalBooking.user.name}}</span>
                    </div>
                    <div class="detail-group">
                      <label>Guests:</label>
                      <span>{{modalBooking.guestCount.adult}} Adults {{modalBooking.guestCount.children !== 0 ? '& ' + modalBooking.guestCount.children + ' Children' : ''}}</span>
                    </div>
                    <div class="detail-group">
                      <label>Check In Date:</label>
                      <span>{{moment(modalBooking.startDate).format('DD-MM-YYYY')}}</span>
                    </div>
                    <div class="detail-group">
                      <label>Check Out Date:</label>
                      <span>{{moment(modalBooking.endDate).format('DD-MM-YYYY')}}</span>
                    </div>
                    <h3>Booking Summary</h3>
                    <div class="detail-group">
                      <label>Hotel:</label>
                      <span>{{modalBooking.hotelRef.hotelName}}</span>
                    </div>
                    <div class="detail-group">
                      <label>Rooms:</label>
                      <div class="rooms-list">
                        <div v-for="item in modalBooking.roomRef" :key="item.roomType._id" class="room-item">
                            <span>{{item.roomType.roomType}} x ({{item.count}})</span> 
                            <small>
                              <span>Price:</span> 
                              <span>₹{{item.roomType.roomPrice}} x {{modalBooking.daysCount}} nights</span>
                            </small>
                            <small>
                              <span>Subtotal:</span>
                              <span>₹{{item.roomType.roomPrice * item.count * modalBooking.daysCount}}</span>
                            </small>
                        </div>
                      </div>
                    </div>
                    <div class="detail-group">
                      <label>Notes:</label>
                      <span>{{modalBooking.note}}</span>
                    </div>
                    <div class="detail-group">
                      <h3>Total:</h3>
                      <h3>₹{{modalBooking.bookingAmount.toLocaleString()}}</h3>
                    </div>
                  </div>
                  <button title="Close (Esc)" type="button" class="mfp-close" @click.prevent="modalBooking = null">×</button>
              </div>
            </div>
          </div>
      </div>
    </div>
  </div>
  `,
  data: {
    loading: false,
    bookings: [],
    currentPage: 0,
    totalItems: 0,
    totalPages: 0,
    modalBooking: null,
  },
  computed: {
    user() {
      return store.state.user;
    },
    moment() {
      return window.moment;
    },
  },
  methods: {
    getBookings() {
      this.loading = true;
      api
        .get(`booking/view?page=${this.currentPage + 1}&size=10`)
        .then(({ data }) => {
          this.loading = false;
          if (data.error) {
            toastError(data.msg);
          } else {
            const { response } = data;
            this.currentPage = response.currentPage;
            this.totalItems = response.totalItems;
            this.totalPages = response.totalPages;
            this.bookings = [...this.bookings, ...response.records];
          }
        })
        .catch((error) => {
          this.loading = false;
          if (error.response) {
            if (error.response) {
              const { msg } = error.response.data;
              toastError(msg);
            } else {
              toastError("Can't get bookings.");
            }
          }
        });
    },
    confirmCancelBooking(bookingId) {
      let notifier = new AWN();
      let onOk = () => {
        this.cancelBooking(bookingId);
      };
      let onCancel = () => {};
      notifier.confirm("Are you sure you want to cancel?", onOk, onCancel, {
        labels: {
          confirm: `CANCEL BOOKING`,
        },
      });
    },
    cancelBooking(bookingId) {
      const body = {
        booking: bookingId,
      };
      this.loading = true;
      api
        .post("booking/cancel/request", body)
        .then(({ data }) => {
          this.loading = false;
          if (data.error) {
            toastError(data.msg);
          } else {
            toastSuccess("Booking cancelled successfully.");
            this.currentPage = 0;
            this.bookings = [];
            this.getBookings();
          }
        })
        .catch((error) => {
          this.loading = false;
          if (error.response) {
            const { msg } = error.response.data;
            toastError(msg);
          } else {
            toastError("Can't cancel booking.");
          }
        });
    },
    showBooking(bookingId) {
      this.modalBooking = this.bookings.find(
        (booking) => booking._id === bookingId
      );
    },
  },
  created() {
    if (this.user !== null) {
      this.getBookings();
    }
  },
});
